import { OICPGatewayCommunication } from './oicp-gateway.interface';

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const pickOicpString = (
  payload: Record<string, unknown>,
  ...keys: string[]
): string | undefined => {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
};

export const getOicpEvcoId = (
  identification?: OICPGatewayCommunication.Identification | Record<string, unknown> | null,
): string | undefined => {
  if (!isRecord(identification)) return undefined;

  for (const key of [
    'RemoteIdentification',
    'PlugAndChargeIdentification',
    'QRCodeIdentification',
    'RFIDIdentification',
  ]) {
    const candidate = identification[key];
    if (!isRecord(candidate)) continue;
    const evcoId = pickOicpString(candidate, 'EvcoID');
    if (evcoId) return evcoId;
  }

  return undefined;
};

export const getOicpUid = (
  identification?: OICPGatewayCommunication.Identification | Record<string, unknown> | null,
): string | undefined => {
  if (!isRecord(identification)) return undefined;

  for (const key of ['RFIDMifareFamilyIdentification', 'RFIDIdentification']) {
    const candidate = identification[key];
    if (!isRecord(candidate)) continue;
    const uid = pickOicpString(candidate, 'UID', 'RFID');
    if (uid) return uid;
  }

  return undefined;
};

export const getOicpBestIdentifier = (
  identification?: OICPGatewayCommunication.Identification | Record<string, unknown> | null,
): string | undefined => {
  return getOicpEvcoId(identification) ?? getOicpUid(identification);
};

export const buildOicpRemoteIdentification = (
  evcoId: string,
): OICPGatewayCommunication.Identification => ({
  RemoteIdentification: { EvcoID: evcoId },
});

export const normalizeEmi3Identifier = (
  value?: string | null,
): string | undefined => {
  const normalized = (value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return normalized.length > 0 ? normalized : undefined;
};

const OICP_ALPHA3_TO_ALPHA2_COUNTRY_CODE: Record<string, string> = {
  AUT: 'AT',
  BEL: 'BE',
  BGR: 'BG',
  CHE: 'CH',
  CZE: 'CZ',
  DEU: 'DE',
  DNK: 'DK',
  ESP: 'ES',
  EST: 'EE',
  FIN: 'FI',
  FRA: 'FR',
  GBR: 'GB',
  GRC: 'GR',
  HRV: 'HR',
  HUN: 'HU',
  IRL: 'IE',
  ITA: 'IT',
  LUX: 'LU',
  NLD: 'NL',
  NOR: 'NO',
  POL: 'PL',
  PRT: 'PT',
  ROU: 'RO',
  SVK: 'SK',
  SVN: 'SI',
  SWE: 'SE',
  UK: 'GB',
};

export const normalizeIsoAlpha2CountryCode = (
  value?: string | null,
): string | undefined => {
  const normalized = (value || '').trim().toUpperCase();
  if (normalized.length === 2) {
    return normalized;
  }
  if (normalized.length === 3) {
    return OICP_ALPHA3_TO_ALPHA2_COUNTRY_CODE[normalized];
  }
  return undefined;
};

const OICP_OPERATOR_ID_REGEX = /^([A-Z]{2})([A-Z0-9]{3})$/;
const OICP_EVSE_ID_REGEX = /^(([A-Z]{2}\*?[A-Z0-9]{3}\*?E[A-Z0-9\*]{1,30})|(\+?[0-9]{1,3}\*[0-9]{3}\*[0-9\*]{1,32}))$/;
const OICP_CHARGING_POOL_ID_REGEX = /^([A-Z]{2}\*?[A-Z0-9]{3}\*?P[A-Z0-9\*]{1,30})$/;

export const formatOicpOperatorId = (
  value?: string | null,
): string | undefined => {
  const normalized = normalizeEmi3Identifier(value);
  if (!normalized) return undefined;

  const match = normalized.match(OICP_OPERATOR_ID_REGEX);
  if (!match) return undefined;

  return `${match[1]}*${match[2]}`;
};

export const isValidOicpEvseId = (value?: string | null): boolean =>
  !!value && OICP_EVSE_ID_REGEX.test(value.trim().toUpperCase());

export const isValidOicpChargingPoolId = (value?: string | null): boolean =>
  !!value && OICP_CHARGING_POOL_ID_REGEX.test(value.trim().toUpperCase());

export const normalizeOicpEvseId = (raw: string): string => {
  const trimmed = raw.trim().toUpperCase();
  const match = trimmed.match(/^([A-Z]{2})[*-]?([A-Z0-9]{3})\*?(E[A-Z0-9*]{1,30})$/);
  if (match) {
    return `${match[1]}${match[2]}*${match[3]}`;
  }
  return trimmed;
};

export const toHubjectOicpEvseId = (
  value?: string | null,
): string | undefined => {
  if (!value) return undefined;

  const normalized = normalizeOicpEvseId(value);
  const match = normalized.match(/^([A-Z]{2})([A-Z0-9]{3})\*(E[A-Z0-9*]{1,30})$/);
  if (!match) return undefined;

  return `${match[1]}*${match[2]}*${match[3]}`;
};

export const buildOicpEvseIds = (
  localId: string | number,
  operatorId?: string | null,
): { evseId: string; evseUid: string } | undefined => {
  const normalizedOperatorId = normalizeEmi3Identifier(operatorId);
  const hubjectOperatorId = formatOicpOperatorId(operatorId);
  if (!normalizedOperatorId || !hubjectOperatorId) {
    return undefined;
  }

  const suffix = `${localId}`
    .replace(/[^A-Za-z0-9*]/g, '')
    .toUpperCase()
    .slice(0, 30);
  if (!suffix) {
    return undefined;
  }

  return {
    evseId: `${normalizedOperatorId}*E${suffix}`,
    evseUid: `${hubjectOperatorId}*E${suffix}`,
  };
};

export const buildOicpChargingPoolId = (
  poolInstance: string,
  operatorId?: string | null,
  countryCode?: string | null,
): string | undefined => {
  const normalizedOperatorId = normalizeEmi3Identifier(operatorId);
  const operatorCountryCode = normalizeIsoAlpha2CountryCode(
    formatOicpOperatorId(operatorId)?.slice(0, 2),
  );
  const alpha2CountryCode =
    normalizeIsoAlpha2CountryCode(countryCode) ?? operatorCountryCode;
  const poolSuffix = poolInstance
    .replace(/[^A-Za-z0-9*]/g, '')
    .toUpperCase()
    .slice(0, 30);

  if (!normalizedOperatorId || !alpha2CountryCode || poolSuffix.length === 0) {
    return undefined;
  }

  const operatorCode = normalizedOperatorId.slice(2, 5);
  if (operatorCode.length !== 3) {
    return undefined;
  }

  return `${alpha2CountryCode}*${operatorCode}*P${poolSuffix}`;
};

const EVCO_ID_REGEX = /^(([A-Za-z]{2}\-?[A-Za-z0-9]{3}\-?C[A-Za-z0-9]{8}\-?[\d|A-Za-z])|([A-Za-z]{2}[\*|\-]?[A-Za-z0-9]{3}[\*|\-]?[A-Za-z0-9]{6}[\*|\-]?[\d|X]))$/;

/** Returns true if the string is a valid OICP EvcoID (ISO or DIN format). */
export const isValidEvcoId = (value?: string | null): boolean =>
  !!value && EVCO_ID_REGEX.test(value);

/**
 * Builds a valid EVCO ID (EMAID) in DIN SPEC 91286 format from an arbitrary
 * internal identifier (e.g. UUID). Format: CC-PPP-XXXXXX-0
 *
 * The instance part is the first 6 uppercase alphanumeric characters of the
 * input after stripping non-alphanumeric characters, padded with '0' if shorter.
 *
 * @param uid - any internal ID (UUID, etc.)
 * @param providerId - OICP provider ID, e.g. "IT-YFL" or "ITYFL". Defaults to env OICP_PROVIDER_ID or "IT-YFL".
 */
export const buildEvcoIdForUser = (
  uid: string,
  providerId?: string,
): string => {
  const pid = ((providerId ?? (typeof process !== 'undefined' ? process.env?.OICP_PROVIDER_ID : undefined)) || 'IT-YFL').toUpperCase();
  const match = pid.match(/^([A-Z]{2})[\-\*]?([A-Z0-9]{3})$/);
  const cc = match?.[1] ?? 'IT';
  const ppp = match?.[2] ?? 'YFL';
  const instance = uid.replace(/[^A-Za-z0-9]/g, '').substring(0, 6).toUpperCase().padEnd(6, '0');
  return `${cc}-${ppp}-${instance}-0`;
};

export const deriveOicpOperatorIdFromEvseId = (
  evseId?: string | null,
): string | undefined => {
  if (!evseId) return undefined;
  const normalized = normalizeOicpEvseId(evseId);
  const match = normalized.match(/^([A-Z]{2})([A-Z0-9]{3})\*/);
  if (!match) return undefined;
  return `${match[1]}*${match[2]}`;
};
